import { AxiosPromise } from 'axios';
import makeRequest from './client';
import {
  FormulationDto,
  RouteDto,
  ErrorResponse,
  UpsertEmaProductRequest,
  UpsertEmaProductResponse
} from '../types/dtos/ema';
import HttpStatus from '../types/enum/http_status';

const Routes = {
  formulations: '/ema/formulations',
  routes: '/ema/routes'
};

export const getEMAData = () => makeRequest(`/ema/ema_records`, 'GET');

export const createEMAData = (payload: any) => makeRequest(`/ema/add_record`, 'POST', payload);

export const updateEMAData = (id: number, payload: any) =>
  makeRequest(`/ema/ema_records/${id}`, 'PUT', payload);

export const deleteEMAData = (id: number) => makeRequest(`/ema/ema_records/${id}`, 'DELETE');

export const updateEMAKeyStatus = (key: string, payload: any) =>
  makeRequest(`/ema/keys/${key}`, 'POST', payload);

interface EMAKeysParams {
  curationType: string;
  status?: string;
  applicationNumber?: string;
}

export const getEMAKeys = (params: EMAKeysParams) => {
  const { curationType, status, applicationNumber } = params;

  const baseURL = '/ema/keys';
  const queryParams = [
    `curation_type=${curationType}`,
    status && `qc_status=${status}`,
    applicationNumber && `application_number=${applicationNumber}`
  ].filter(Boolean); // Filter out undefined or falsey values

  const finalURL = `${baseURL}?${queryParams.join('&')}`;
  return makeRequest(finalURL, 'GET');
};

export const triggerEMADag = (payload: any) => {
  return makeRequest(`/ema/trigger`, 'POST', payload);
};

export const getEmaFormulations = (): AxiosPromise<FormulationDto[]> =>
  makeRequest(Routes.formulations, 'GET');

export const getEmaRoutes = (): AxiosPromise<RouteDto[]> => makeRequest(Routes.routes, 'GET');

const handleCreationError = (
  error: Error | ErrorResponse,
  entity: 'formulation' | 'route'
): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (!('code' in error)) {
    return `An unknown error occurred while creating ${entity} - Please try again later`;
  }
  switch (error.code) {
    case HttpStatus.Conflict:
      /* eslint-disable no-case-declarations */
      let message = `The ${entity} already exists`;
      try {
        const existingEntity = JSON.parse(error.message);
        message += ` - Check ${existingEntity.value} in the dropdown`;
      } catch (nestedError) {
        return message;
      }
      return message;
    case HttpStatus.BadRequest:
      return `Error: ${error.message}`;
    default:
      return `An unknown error occurred while creating ${entity} - Please try again later`;
  }
};

/**
 * @param {string} newFormulation The new formulation to create
 * @param {string} curatorEmail The email of the curator creating the formulation
 * @param {string} curatorName The name of the curator creating the formulation
 * @returns {Promise<FormulationDto | { error: string }>} The created formulation or an error message
 */
export const createEmaFormulation = async (
  newFormulation: string,
  curatorEmail: string,
  curatorName: string
): Promise<FormulationDto | { error: string }> => {
  try {
    const response = await makeRequest(Routes.formulations, 'POST', {
      value: newFormulation,
      created_by_email: curatorEmail,
      created_by_name: curatorName
    });
    if (response.data?.error) {
      return { error: handleCreationError(response.data, 'formulation') };
    }
    if ('status' in response && response.status !== HttpStatus.Created) {
      return { error: handleCreationError(Error(response.data.error), 'formulation') };
    }
    return response.data.body;
  } catch (error) {
    return { error: handleCreationError(error as Error, 'formulation') };
  }
};

const handleProductError = (error: Error | ErrorResponse): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (!('code' in error)) {
    return 'An unknown error occurred while saving the product - Please try again later';
  }
  switch (error.code) {
    case HttpStatus.BadRequest:
      return `Error: ${error.message}`;
    case HttpStatus.InternalServerError:
      return 'A server error occurred while saving the product - Please try again later';
    default:
      return 'An unknown error occurred while saving the product - Please try again later';
  }
};

/**
 * @param {UpsertEmaProductRequest} payload The product data to create or update
 * @returns {Promise<UpsertEmaProductResponse | { error: string }>} The saved product id or an error message
 */
export const upsertEmaProduct = async (
  payload: UpsertEmaProductRequest
): Promise<UpsertEmaProductResponse | { error: string }> => {
  try {
    const response = await makeRequest('/ema/products_qcd', 'PUT', payload);
    if (response.data?.error) {
      return { error: handleProductError(response.data) };
    }
    if ('status' in response && response.status !== HttpStatus.OK) {
      return { error: handleProductError(Error(response.data.error)) };
    }
    return response.data.body as UpsertEmaProductResponse;
  } catch (error) {
    return { error: handleProductError(error as Error | ErrorResponse) };
  }
};

/**
 * @param {string} newRoute The new route to create
 * @param {string} curatorEmail The email of the curator creating the route
 * @param {string} curatorName The name of the curator creating the route
 * @returns {Promise<RouteDto | { error: string }>} The created route or an error message
 */
export const createEmaRoute = async (
  newRoute: string,
  curatorEmail: string,
  curatorName: string
): Promise<RouteDto | { error: string }> => {
  try {
    const response = await makeRequest(Routes.routes, 'POST', {
      value: newRoute,
      created_by_email: curatorEmail,
      created_by_name: curatorName
    });
    if (response.data?.error) {
      return { error: handleCreationError(response.data, 'route') };
    }
    if ('status' in response && response.status !== HttpStatus.Created) {
      return { error: handleCreationError(Error(response.data.error), 'route') };
    }
    return response.data.body;
  } catch (error) {
    return { error: handleCreationError(error as Error, 'route') };
  }
};
